<script setup>
import { ref, watch, nextTick, computed } from 'vue'
import { useQuasar } from 'quasar'
import { useI18n } from 'vue-i18n'
import Hls from 'hls.js'
import apiAddress from '../../../api/address.api'
import apiDevice from '../../../api/device.api'
import apiCustomer from '../../../api/customer.api'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  device: {
    type: Object,
    default: null,
  },
  address: {
    type: Object,
    default: null,
  },
})

const emit = defineEmits(['update:modelValue'])

const { t } = useI18n()
const $q = useQuasar()

const devicePhones = ref([])
const newPhone = ref('')
const loading = ref(false)
const newApartment = ref('')
const newPermitted = ref(false)
const suggestions = ref([])
const suggestionsLoading = ref(false)
const showSuggestions = ref(false)
let suggestTimer = null

watch(newPhone, (val) => {
  clearTimeout(suggestTimer)
  if (!val || !val.trim()) {
    suggestions.value = []
    showSuggestions.value = false
    return
  }
  suggestTimer = setTimeout(async () => {
    try {
      suggestionsLoading.value = true
      const { data } = await apiCustomer.getSimpleList({ phone: val.trim(), 'page-size': 5 })
      suggestions.value = data ?? []
      showSuggestions.value = suggestions.value.length > 0
    } catch {
      suggestions.value = []
      showSuggestions.value = false
    } finally {
      suggestionsLoading.value = false
    }
  }, 300)
})

function selectSuggestion(customer) {
  newPhone.value = customer.phone
  suggestions.value = []
  showSuggestions.value = false
}
const cameraUrl = ref(null)
const cameraLoading = ref(false)
const videoRef = ref(null)
let hlsInstance = null
let cameraRequestId = 0
const isOnline = ref(false)
const isCameraOnline = ref(false)
const p2pLoading = ref(false)
const p2pActive = ref(false)
const p2pStatus = ref('')
let p2pSession = null
const barrierType = ref('entranceBarrier')
const barrierTypeOptions = computed(() => [
  { label: t('addresses.permit_dialog.entrance_barrier'), value: 'entranceBarrier' },
  { label: t('addresses.permit_dialog.exit_barrier'), value: 'exitBarrier' },
])

const H264_PT = 109
const H264_FMTP = 'level-asymmetry-allowed=1;packetization-mode=1;profile-level-id=42e01f'

watch(
  () => props.modelValue,
  (val) => {
    if (val && props.device) {
      devicePhones.value = buildPhoneList()
      newPhone.value = ''
      newApartment.value = ''
      newPermitted.value = false
      suggestions.value = []
      showSuggestions.value = false
      barrierType.value = 'entranceBarrier'
      cameraUrl.value = null
      cameraLoading.value = false
      stopP2P(true)
      cameraRequestId++
      destroyHls()
      isOnline.value = props.device.isOnline ?? false
      isCameraOnline.value = props.device.isCameraOnline ?? false
    }
  }
)

function buildPhoneList() {
  if (!props.device?._id) return []
  const customers = props.address?.customers ?? []
  const deviceId = String(props.device._id)
  return customers
    .filter((c) => Array.isArray(c.devices) && c.devices.some((d) => String(d.deviceOId) === deviceId))
    .map((c) => {
      const entry = c.devices.find((d) => String(d.deviceOId) === deviceId)
      return {
        phone: c.phone,
        name: c.name,
        apartment: entry?.apartment || '',
        permitted: entry?.permitted ?? false,
      }
    })
    .filter((c) => c.phone)
}

function chipLabel(item) {
  console.log('Customer chip item: name=', item.name, 'phone=', item.phone, 'apartment=', item.apartment, 'permitted=', item.permitted)
  let label = item.phone
  if (item.name) label += ` · ${item.name}`
  if (item.apartment) label += ` · ${t('addresses.permit_dialog.apartmentNumber', { number: item.apartment })}`
  return label
}

async function addPhonePermit() {
  if (!props.device?._id || !props.address?._id) return
  const phone = newPhone.value.trim()
  if (!phone) return
  try {
    loading.value = true
    const apartment = newApartment.value.trim()
    const permitted = newPermitted.value
    await apiAddress.addPermit({ addressId: props.address._id, devices: [{ deviceOId: props.device._id, apartment, permitted }], phone })
    if (!devicePhones.value.some((p) => p.phone === phone)) {
      devicePhones.value.push({ phone, apartment, permitted })
    }
    newPhone.value = ''
    newApartment.value = ''
    newPermitted.value = false
    $q.notify({ type: 'positive', message: t('addresses.permit_dialog.customer_added') })
  } catch (error) {
    $q.notify({ type: 'negative', message: error?.data?.error || t('addresses.permit_dialog.failed_add') })
  } finally {
    loading.value = false
  }
}

async function removePhonePermit(phone) {
  if (!props.device?._id || !props.address?._id) return
  try {
    loading.value = true
    await apiAddress.removePermit({ addressId: props.address._id, devices: [{ deviceOId: props.device._id, apartment: '', permitted: false }], phone })
    devicePhones.value = devicePhones.value.filter((v) => v.phone !== phone)
    $q.notify({ type: 'positive', message: t('addresses.permit_dialog.customer_removed') })
  } catch (error) {
    $q.notify({ type: 'negative', message: error?.data?.error || t('addresses.permit_dialog.failed_remove') })
  } finally {
    loading.value = false
  }
}

async function openDevice() {
  try {
    loading.value = true
    const payload = props.device?.type === 'PARKINGMETRIX-BARRIER'
      ? { barrierType: barrierType.value }
      : undefined
    await apiDevice.open(props.device._id, payload)
    $q.notify({ type: 'positive', message: t('addresses.permit_dialog.opened') })
  } catch (error) {
    $q.notify({ type: 'negative', message: error?.data?.error || t('addresses.permit_dialog.failed_open') })
  } finally {
    loading.value = false
  }
}

async function closeDevice() {
  try {
    loading.value = true
    const payload = props.device?.type === 'PARKINGMETRIX-BARRIER'
      ? { barrierType: barrierType.value }
      : undefined
    await apiDevice.close(props.device._id, payload)
    $q.notify({ type: 'positive', message: t('addresses.permit_dialog.closed') })
  } catch (error) {
    $q.notify({ type: 'negative', message: error?.data?.error || t('addresses.permit_dialog.failed_close') })
  } finally {
    loading.value = false
  }
}

async function checkStatus() {
  try {
    loading.value = true
    const res = await apiDevice.status(props.device._id)
    isOnline.value = res?.data?.online ?? false
    isCameraOnline.value = res?.data?.isCameraOnline ?? false
    $q.notify({
      type: isOnline.value ? 'positive' : 'warning',
      message: isOnline.value
        ? t('addresses.permit_dialog.status_online')
        : t('addresses.permit_dialog.status_offline'),
    })
  } catch (error) {
    $q.notify({ type: 'negative', message: error?.data?.error || t('addresses.permit_dialog.failed_status') })
  } finally {
    loading.value = false
  }
}

async function loadCamera() {
  if (cameraLoading.value) return
  stopP2P(true)
  const requestId = ++cameraRequestId
  try {
    cameraLoading.value = true
    const res = await apiDevice.camera(props.device._id)
    if (requestId !== cameraRequestId) return
    cameraUrl.value = res?.data?.url ?? null
    if (cameraUrl.value) {
      await nextTick()
      initHls(cameraUrl.value)
    }
  } catch (error) {
    if (requestId !== cameraRequestId) return
    $q.notify({ type: 'negative', message: error?.data?.error || t('addresses.permit_dialog.failed_camera') })
  } finally {
    if (requestId === cameraRequestId) cameraLoading.value = false
  }
}

function resolveStreamType(webrtcConfig) {
  let streamTypes = []

  try {
    const skillRaw = webrtcConfig?.skill
    if (typeof skillRaw === 'string' && skillRaw.trim()) {
      const parsed = JSON.parse(skillRaw)
      const videos = Array.isArray(parsed?.videos) ? parsed.videos : []
      streamTypes = videos
        .map((v) => Number(v?.streamType))
        .filter((v, idx, arr) => Number.isFinite(v) && v > 0 && arr.indexOf(v) === idx)
    }
  } catch {
    // ignore malformed skill
  }

  const clarityCandidates = [Number(webrtcConfig?.video_clarity), Number(webrtcConfig?.vedio_clarity)]
  for (const c of clarityCandidates) {
    if (Number.isFinite(c) && c > 0 && streamTypes.includes(c)) return c
  }

  if (streamTypes.includes(4)) return 4
  if (streamTypes.includes(2)) return 2
  if (streamTypes.length > 0) return streamTypes[0]

  return 1
}

function buildStreamAttempts(primary) {
  const attempts = [primary, 4, 2, 1]
  const uniq = []
  for (const v of attempts) {
    const n = Number(v)
    if (!Number.isFinite(n) || n <= 0) continue
    if (!uniq.includes(n)) uniq.push(n)
  }
  return uniq
}

function randomId() {
  if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID()
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`
}

function wsBaseFromApi(apiBase) {
  return String(apiBase || '')
    .replace(/^http:/, 'ws:')
    .replace(/^https:/, 'wss:')
    .replace(/\/api.*$/, '')
}

function waitForWsMessage(ws, predicate, timeoutMs = 15000) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      ws.removeEventListener('message', onMessage)
      reject(new Error('WebSocket message timeout'))
    }, timeoutMs)

    function onMessage(event) {
      try {
        const msg = JSON.parse(event.data)
        if (predicate(msg)) {
          clearTimeout(timer)
          ws.removeEventListener('message', onMessage)
          resolve(msg)
        }
      } catch {
        // ignore malformed packets
      }
    }

    ws.addEventListener('message', onMessage)
  })
}

function waitForVideoTrack(stream, timeoutMs = 8000) {
  return new Promise((resolve) => {
    if (stream.getVideoTracks().length > 0) {
      resolve(true)
      return
    }

    const timer = setTimeout(() => {
      stream.removeEventListener('addtrack', onAddTrack)
      resolve(false)
    }, timeoutMs)

    function onAddTrack(event) {
      if (event?.track?.kind === 'video') {
        clearTimeout(timer)
        stream.removeEventListener('addtrack', onAddTrack)
        resolve(true)
      }
    }

    stream.addEventListener('addtrack', onAddTrack)
  })
}

function mungeOfferToH264Only(sdp) {
  const lines = sdp.split('\r\n')
  const out = []
  let inVideo = false

  const appendH264Lines = () => {
    out.push(`a=rtpmap:${H264_PT} H264/90000`)
    out.push(`a=fmtp:${H264_PT} ${H264_FMTP}`)
    out.push(`a=rtcp-fb:${H264_PT} goog-remb`)
    out.push(`a=rtcp-fb:${H264_PT} transport-cc`)
    out.push(`a=rtcp-fb:${H264_PT} ccm fir`)
    out.push(`a=rtcp-fb:${H264_PT} nack`)
    out.push(`a=rtcp-fb:${H264_PT} nack pli`)
  }

  for (const line of lines) {
    if (line.startsWith('m=video ')) {
      inVideo = true
      const parts = line.split(' ')
      out.push(`${parts[0]} ${parts[1]} ${parts[2]} ${H264_PT}`)
      continue
    }

    if (line.startsWith('m=')) {
      if (inVideo) appendH264Lines()
      inVideo = false
      out.push(line)
      continue
    }

    if (inVideo) {
      if (/^a=rtpmap:\d+\s+/i.test(line)) continue
      if (/^a=fmtp:\d+\s+/i.test(line)) continue
      if (/^a=rtcp-fb:\d+\s+/i.test(line)) continue
    }

    out.push(line)
  }

  if (inVideo) appendH264Lines()
  return out.join('\r\n')
}

function answerHasNegotiableVideo(answerSdp) {
  const lines = String(answerSdp || '').split('\r\n')
  const mVideo = lines.find((l) => l.startsWith('m=video '))
  if (!mVideo) return false

  const mParts = mVideo.split(' ')
  const videoPts = new Set(mParts.slice(3).filter(Boolean))
  if (videoPts.size === 0 || [...videoPts].every((pt) => pt === '0')) return false

  const h264Pts = new Set(
    lines
      .map((l) => l.match(/^a=rtpmap:(\d+)\s+([^\s]+)/i))
      .filter(Boolean)
      .filter((m) => String(m[2] || '').toUpperCase().startsWith('H264/'))
      .map((m) => m[1])
  )

  for (const pt of h264Pts) {
    if (videoPts.has(pt)) return true
  }

  return false
}

async function startP2PPreview() {
  if (!props.device?._id || p2pLoading.value) return

  const baseApi = import.meta.env.VITE_API_SERVER
  if (!baseApi) {
    $q.notify({ type: 'negative', message: t('addresses.permit_dialog.failed_p2p') })
    return
  }

  try {
    p2pLoading.value = true
    p2pStatus.value = t('addresses.permit_dialog.p2p_connecting')
    cameraRequestId++
    destroyHls()
    stopP2P(true)

    const initRes = await apiDevice.p2pInit(props.device._id)
    const initData = initRes?.data ?? initRes
    if (!initData?.ok || !initData?.deviceId || !initData?.webrtcConfig) {
      throw new Error('p2p-init failed')
    }

    const deviceId = initData.deviceId
    const webrtcConfig = initData.webrtcConfig
    const streamType = resolveStreamType(webrtcConfig)
    const streamAttempts = buildStreamAttempts(streamType)
    const wsUrl = `${wsBaseFromApi(baseApi)}/ws?deviceId=${deviceId}`

    const ws = new WebSocket(wsUrl)
    await new Promise((resolve, reject) => {
      ws.addEventListener('open', resolve, { once: true })
      ws.addEventListener('error', reject, { once: true })
    })

    await waitForWsMessage(ws, (m) => m.type === 'session-ready', 15000)

    const ices = webrtcConfig?.p2p_config?.ices ?? []
    const iceServers = ices.map((ice) => ({
      urls: ice.urls,
      username: ice.username,
      credential: ice.credential,
    }))

    const pc = new RTCPeerConnection({
      iceServers: iceServers.length ? iceServers : [{ urls: 'stun:stun.l.google.com:19302' }],
    })

    const remoteStream = new MediaStream()
    if (videoRef.value) {
      videoRef.value.srcObject = remoteStream
      videoRef.value.play().catch(() => { })
    }

    const session = {
      ws,
      pc,
      deviceId,
      sessionId: null,
      stopped: false,
    }
    p2pSession = session

    pc.addTransceiver('video', { direction: 'recvonly' })
    pc.addTransceiver('audio', { direction: 'recvonly' })

    pc.ontrack = (event) => {
      if (event.track && !remoteStream.getTracks().some((t) => t.id === event.track.id)) {
        remoteStream.addTrack(event.track)
      }
      if (videoRef.value && videoRef.value.srcObject !== remoteStream) {
        videoRef.value.srcObject = remoteStream
        videoRef.value.play().catch(() => { })
      }
    }

    pc.onicecandidate = (event) => {
      if (!session.sessionId || ws.readyState !== WebSocket.OPEN) return
      const candidate = event.candidate ? `a=${event.candidate.candidate}` : ''
      ws.send(JSON.stringify({
        action: 'candidate',
        deviceId,
        sessionId: session.sessionId,
        candidate,
      }))
    }

    ws.addEventListener('message', async (event) => {
      if (session.stopped) return
      try {
        const msg = JSON.parse(event.data)
        const t = msg?.payload?.data?.header?.type
        const sid = msg?.payload?.data?.header?.sessionid
        if (msg.type === 'tuya-signal' && t === 'candidate' && sid === session.sessionId) {
          const c = String(msg?.payload?.data?.msg?.candidate || '').replace(/^a=/, '').trim()
          if (c) {
            await pc.addIceCandidate({ candidate: c, sdpMid: '0', sdpMLineIndex: 0 })
          }
        }
      } catch {
        // ignore parsing/candidate errors
      }
    })

    let negotiated = false

    for (let i = 0; i < streamAttempts.length; i++) {
      if (session.stopped || p2pSession !== session) break

      const st = streamAttempts[i]
      session.sessionId = randomId()
      p2pStatus.value = `${t('addresses.permit_dialog.p2p_connecting')} (${st})`

      const offer = await pc.createOffer()
      await pc.setLocalDescription(offer)
      const sdp = mungeOfferToH264Only(offer.sdp)

      ws.send(JSON.stringify({
        action: 'offer',
        deviceId,
        sessionId: session.sessionId,
        sdp,
        streamType: st,
      }))

      const signalMsg = await waitForWsMessage(ws, (m) => {
        if (m.type !== 'tuya-signal') return false
        const header = m.payload?.data?.header
        const type = header?.type
        return header?.sessionid === session.sessionId && (type === 'answer' || type === 'disconnect')
      }, 20000).catch(() => null)

      if (!signalMsg) continue

      if (signalMsg?.payload?.data?.header?.type === 'disconnect') {
        continue
      }

      const answerSdp = signalMsg?.payload?.data?.msg?.sdp || ''
      if (!answerHasNegotiableVideo(answerSdp)) {
        continue
      }

      try {
        await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp })
      } catch {
        continue
      }

      const gotVideo = await waitForVideoTrack(remoteStream, 8000)
      const hasMoreAttempts = i < streamAttempts.length - 1
      if (!gotVideo && hasMoreAttempts) {
        ws.send(JSON.stringify({ action: 'disconnect', deviceId, sessionId: session.sessionId }))
        continue
      }

      negotiated = true
      p2pActive.value = true
      p2pStatus.value = `${t('addresses.permit_dialog.p2p_connected')} (${st})`
      break
    }

    if (!negotiated) {
      throw new Error('Failed to negotiate P2P video stream')
    }
  } catch (error) {
    p2pActive.value = false
    p2pStatus.value = ''
    stopP2P(true)
    $q.notify({ type: 'negative', message: error?.data?.error || t('addresses.permit_dialog.failed_p2p') })
  } finally {
    p2pLoading.value = false
  }
}

function stopP2P(silent = false) {
  const session = p2pSession
  p2pSession = null
  p2pActive.value = false
  p2pLoading.value = false
  p2pStatus.value = ''

  const deviceId = props.device?._id
  const hasCamera = Boolean(props.device?.deviceCameraId)
  if (deviceId && hasCamera) {
    apiDevice
      .p2pStop(deviceId, session?.sessionId)
      .catch(() => {
        // local teardown still proceeds even if stop endpoint fails
      })
  }

  if (session) {
    session.stopped = true
    try {
      if (session.ws?.readyState === WebSocket.OPEN && session.sessionId) {
        session.ws.send(JSON.stringify({
          action: 'disconnect',
          deviceId: session.deviceId,
          sessionId: session.sessionId,
        }))
      }
    } catch {
      // ignore
    }

    try { session.pc?.close() } catch { }
    try { session.ws?.close() } catch { }
  }

  if (videoRef.value) {
    videoRef.value.pause()
    videoRef.value.srcObject = null
    if (!cameraUrl.value) {
      videoRef.value.removeAttribute('src')
      videoRef.value.load()
    }
  }

  if (!silent) {
    $q.notify({ type: 'info', message: t('addresses.permit_dialog.p2p_stop') })
  }
}

function initHls(url) {
  if (hlsInstance) {
    hlsInstance.destroy()
    hlsInstance = null
  }
  const video = videoRef.value
  if (!video) return
  if (Hls.isSupported()) {
    hlsInstance = new Hls()
    hlsInstance.loadSource(url)
    hlsInstance.attachMedia(video)
    hlsInstance.on(Hls.Events.MANIFEST_PARSED, () => video.play().catch(() => { }))
  } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
    // Safari native HLS
    video.src = url
    video.play().catch(() => { })
  }
}

function destroyHls() {
  if (hlsInstance) {
    hlsInstance.destroy()
    hlsInstance = null
  }
  if (videoRef.value) {
    videoRef.value.pause()
    videoRef.value.removeAttribute('src')
    videoRef.value.load()
  }
  cameraUrl.value = null
}

function onDialogHide() {
  destroyHls()
  stopP2P(true)
}
</script>

<template>
  <q-dialog :model-value="modelValue" @update:model-value="emit('update:modelValue', $event)" @hide="onDialogHide">
    <q-card style="width: 480px; max-width: 95vw;">
      <!-- Header -->
      <q-card-section class="row items-center q-gutter-sm">
        <div class="text-h6 col">{{ device?.name }}</div>
        <q-chip v-if="device?.type" dense color="primary" text-color="white">
          {{ device.type }}
        </q-chip>
        <q-chip dense :color="isOnline ? 'positive' : 'grey-6'" text-color="white" icon="wifi"
          :label="isOnline ? t('addresses.permit_dialog.status_online') : t('addresses.permit_dialog.status_offline')" />
        <q-chip v-if="device?.deviceCameraId" dense :color="isCameraOnline ? 'positive' : 'grey-6'" text-color="white"
          icon="videocam" :label="t('addresses.permit_dialog.camera')" />
      </q-card-section>

      <q-separator />

      <!-- Camera preview -->
      <template v-if="device?.deviceCameraId">
        <q-card-section class="q-pa-sm">
          <div class="text-subtitle2 q-mb-xs">{{ t('addresses.permit_dialog.camera') }}</div>
          <div v-if="!cameraUrl && !p2pActive && !p2pLoading" class="row items-center justify-center rounded-borders"
            :class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-3'" style="height: 160px; cursor: pointer;"
            @click="loadCamera">
            <q-spinner v-if="cameraLoading" color="primary" size="32px" />
            <q-icon v-else name="play_circle" size="48px" color="grey-6" />
          </div>
          <div v-else style="position: relative;">
            <video ref="videoRef"
              style="width: 100%; border-radius: 4px; display: block; max-height: 240px; background: #000;" controls
              playsinline muted />
            <q-btn flat dense round icon="close" size="sm" color="white"
              style="position: absolute; top: 4px; right: 4px; background: rgba(0,0,0,0.5);" @click="destroyHls" />
          </div>
          <div class="row q-gutter-sm q-mt-sm">
            <q-btn dense outline color="primary" icon="play_arrow" :label="t('addresses.permit_dialog.camera')"
              :loading="cameraLoading" :disable="p2pLoading" @click="loadCamera" />
            <q-btn dense outline color="secondary" icon="wifi_tethering" :label="t('addresses.permit_dialog.p2p_start')"
              :loading="p2pLoading" :disable="cameraLoading || p2pActive" @click="startP2PPreview" />
            <q-btn v-if="p2pActive" dense flat color="negative" icon="stop"
              :label="t('addresses.permit_dialog.p2p_stop')" @click="stopP2P()" />
          </div>
          <div v-if="p2pStatus" class="text-caption text-grey-6 q-mt-xs">
            {{ p2pStatus }}
          </div>
        </q-card-section>
        <q-separator />
      </template>

      <!-- Customers -->
      <q-card-section>
        <div class="text-subtitle2 q-mb-sm">{{ t('addresses.permit_dialog.customers') }}</div>
        <div v-if="devicePhones.length" class="column q-gutter-xs">
          <div v-for="item in devicePhones" :key="item.phone" class="row items-center justify-between">
            <q-chip :color="(typeof item.name === 'string') ? (item.permitted ? 'green-1' : 'red-1') : (item.permitted ? 'grey-3' : 'grey-9')"
              :text-color="(typeof item.name === 'string') ? (item.permitted ? 'green-9' : 'red-9') : (item.permitted ? 'grey-9' : 'white')">
              {{ chipLabel(item) }}
            </q-chip>
            <q-btn dense flat color="negative" icon="delete" :disable="loading"
              @click="removePhonePermit(item.phone)" />
          </div>
        </div>
        <div v-else class="text-grey-6">{{ t('addresses.permit_dialog.no_customers') }}</div>

        <div class="column q-gutter-sm q-mt-md">
          <div class="row q-col-gutter-sm">
            <div class="col">
              <div style="position: relative;">
                <q-input v-model="newPhone" outlined dense :label="t('addresses.permit_dialog.phone')" :disable="loading"
                  :loading="suggestionsLoading" autocomplete="off" @keyup.enter="addPhonePermit"
                  @blur="setTimeout(() => { showSuggestions = false }, 150)"
                  @focus="showSuggestions = suggestions.length > 0" />
                <q-list v-if="showSuggestions" bordered separator
                  :class="$q.dark.isActive ? 'bg-grey-9 text-white' : 'bg-white text-dark'"
                  style="position: absolute; z-index: 9999; width: 100%; border-radius: 4px; box-shadow: 0 4px 12px rgba(0,0,0,0.4);">
                  <q-item v-for="customer in suggestions" :key="String(customer._id)" clickable
                    :class="$q.dark.isActive ? 'text-white' : 'text-dark'"
                    @mousedown.prevent="selectSuggestion(customer)">
                    <q-item-section>
                      <q-item-label>{{ customer.phone }}</q-item-label>
                      <q-item-label v-if="customer.name" caption
                        :class="$q.dark.isActive ? 'text-grey-4' : 'text-grey-7'">{{ customer.name }}</q-item-label>
                    </q-item-section>
                  </q-item>
                </q-list>
              </div>
            </div>
            <div class="col">
              <q-input v-model="newApartment" outlined dense :label="t('addresses.permit_dialog.apartment')"
                :disable="loading" autocomplete="off" @keyup.enter="addPhonePermit" />
            </div>
          </div>
          <div class="row items-center justify-between">
            <q-toggle v-model="newPermitted" color="positive" :disable="loading"
              :label="newPermitted ? t('customer.permitted') : t('customer.not_permitted')" />
            <q-btn color="primary" :label="t('addresses.permit_dialog.add')" :loading="loading"
              @click="addPhonePermit" />
          </div>
        </div>
      </q-card-section>

      <q-separator />

      <!-- Actions -->
      <q-card-actions>
        <q-select v-if="device?.type === 'PARKINGMETRIX-BARRIER'" v-model="barrierType" dense outlined emit-value
          map-options :options="barrierTypeOptions" :label="t('addresses.permit_dialog.barrier_type')"
          :disable="loading" style="min-width: 210px;" />
        <q-btn flat color="positive" icon="lock_open" :label="t('addresses.permit_dialog.open')" :loading="loading"
          @click="openDevice" />
        <q-btn flat color="negative" icon="lock" :label="t('addresses.permit_dialog.close')" :loading="loading"
          @click="closeDevice" />
        <q-btn flat color="secondary" icon="wifi" :label="t('addresses.permit_dialog.check_status')" :loading="loading"
          @click="checkStatus" />
        <q-space />
        <q-btn flat :label="t('actions.cancel')" @click="emit('update:modelValue', false)" />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>
